import _                                               from 'underscore';
import ApolloClient, { DocumentNode, PresetConfig }    from 'apollo-boost';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { produce }                                     from 'immer';

import introspectionQueryResultData from '../types/graphql.ts';

import { _resolvers } from './_resolvers';
import { _typeDefs }  from './_typeDefs';
import { _state }     from './_state';
import { UserStore }  from './_stores';

import * as HeadersUtility     from '../util/network/HeadersUtility';
import { NETWORK_ERROR_CODES } from '../constants/SystemEnums';
import { EnvironmentObject }   from '../environments/types/base';
import DefaultClient           from 'apollo-boost';
import { KeyedObject }         from '../types';
import { GraphQLError }        from 'graphql';
import { ErrorService }        from '../services/ErrorService';

export interface CorvalApolloClient extends DefaultClient<any> {

	getState( query: DocumentNode ): unknown,

	writeState( state: KeyedObject ): void,

	getInitialState(): unknown,
}

export function createClient( customSettings: PresetConfig = {} ){

	// Due to union types within schema, we need to create a matcher
	// based on typenames that help Apollo return the right thing.
	const fragmentMatcher = new IntrospectionFragmentMatcher( {
		introspectionQueryResultData
	} )

	//@ts-ignore
	let environment            = __ENV__,
	    settings: PresetConfig = {

			// Include fragment matcher within the cache
		    cache: new InMemoryCache( { fragmentMatcher } ),

		    // Setup custom url display
		    uri: operation => {

			    // graphql url from API schema + operation name
			    return `${ environment.api.graphql }/${ operation.operationName }`;
		    },

		    // Add additional requests
		    request: ( operation ) => {

			    // set up context headers to have an auth token if it's available
			    operation.setContext( {
				    headers: { ...HeadersUtility.generateAuthHeaderEntries() }
			    } );
		    },

		    onError:     ( errorObject ) => {

			    /* errorObject = {
			        graphQLErrors:  GraphQLError[],
			        networkError:   Error,
			        response?:      ExecutionResult,
			        operation:      Operation
		    	}*/

			    let errors = [
				    ...( errorObject.graphQLErrors ?? [] ),
				    ...( errorObject.networkError ? ErrorService.maybeGetGraphQLNetworkErrors( errorObject ) ?? [] : [] )
			    ];

			    let serverErrorRespondedWithIsUnauthenticated = errorObject.networkError?.response?.statusText === "Unauthorized";

			    let unauthenticatedErrors = errors.filter( ( error: Error ) => {

				    // Does this error have an extension code?
				    let errorCode = _.property( [ 'extensions', 'code' ] )( error ) ?? null;

				    if( errorCode ){

					    // If so, see if it's an unauthorized type...
					    return [
						    NETWORK_ERROR_CODES.AUTH_NOT_AUTHENTICATED,
						    NETWORK_ERROR_CODES.AUTH_NOT_AUTHORIZED,
					    ].includes( errorCode );
				    }

				    // Otherwise we don't know what the error is yet
				    return false;
			    } );

			    if( unauthenticatedErrors.length || serverErrorRespondedWithIsUnauthenticated ){

				    console.warn( `👤 Unauthenticated request made` );

			    } else {

				    console.error( 'Errors occurred while attempting GraphQL request' );

				    console.table( errors.map( error => ( { message: error.message } ) ) );
			    }

		    },
		    clientState: {
			    defaults:  _state,
			    resolvers: {},
			    typeDefs:  _typeDefs,
		    },

		    ...customSettings
	    };

	// Setup Apollo client
	let client = new ApolloClient( settings ) as CorvalApolloClient;

	// https://github.com/apollographql/react-apollo/issues/3750
	client.defaultOptions = {
		watchQuery: {

			// always call for new data, but use cache if available until the response;
			// if not specified, we fall to 'cache-first', which ain't great.
			// fetchPolicy: 'cache-and-network',
		},
		query:      {

			// always call for new data;
			// if not specified, we fall to 'cache-first', which ain't great.
			// fetchPolicy: 'network-only',
		}
	};

	// Helper function to get data from the cache
	client.getState = ( query ) => {
		return client.cache.readQuery( { query } );
	};

	// Helper function to write data back to the cache
	client.writeState = ( state ) => {
		return client.cache.writeData( { data: state } );
	};

	// Helper function to get initial cache
	client.getInitialState = () => {
		return _state;
	};

	// Add store resolvers
	_resolvers.map( resolver => client.addResolvers( resolver ) );

	return client;
}

export async function hydrateClientState( apolloClient: ApolloClient<any> ){

	let [ user ] = await Promise.all( [
		UserStore.hydrateUserToken( apolloClient )
	] );

	let hydratedStore = produce( _state, draft => {
		draft[ UserStore.storeKey ] = { ...UserStore.state, ...user };
	} );

	apolloClient.cache.writeData( {
		data: hydratedStore
	} );
}
